import React from 'react';
import {
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  PlusIcon,
  TableToolbar,
  TablePagination,
  TablePaginationProps,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { usePagination } from 'hooks/usePagination';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import { formatPaginationRange } from 'components/ApprovalWorkflows/MyRequests/EmployeeBankAccount/utils';
import { RegisteredOffice } from './RegisteredOffice';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { officeLocationQueryKeys } from './data';

export const PreviewOfficeLocations = () => {
  const history = useHistory();
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [officeLocationQueryKeys.GET_OFFICE_LOCATIONS_NON_PRIMARY, currentPage, itemsPerPage],
    queryFn: () =>
      api.officeLocation.fetchOfficeLocations({
        limit: itemsPerPage,
        page: currentPage + 1,
        isPrimary: false,
      }),
  });

  const onPageChange = ({ page }: { page: number }) => {
    setCurrentPage(page);
  };

  const onPageSizeChange = ({ pageSize }: { pageSize: number }) => {
    setItemsPerPage(pageSize);
    setCurrentPage(0);
  };

  const handleAddOfficeLocation = () => {
    history.push(routePaths.officeLocation.create);
  };

  const handleClose = () => {
    window.location.href = routePaths.phpRoutes.companyDetails;
  };

  const officeLocations = data?.data ?? [];

  const shouldShowEmptyView = !isError && !isLoading && !isFetching && officeLocations.length === 0;
  const shouldShowErrorView = isError;

  return (
    <Modal isOpen={true} onDismiss={handleClose} size="full">
      <ModalHeader title="Office Locations" />
      <ModalBody padding="spacing.0">
        <Box padding="spacing.7">
          <RegisteredOffice />
          {shouldShowErrorView ? (
            <ErrorView
              onRefetch={refetch}
              borderWidth="none"
              backgroundColor="surface.background.gray.intense"
            />
          ) : shouldShowEmptyView ? (
            <EmptyListView
              title="No office locations found"
              description="Add your first office location to get started">
              <Box marginTop="spacing.6" display="flex" justifyContent="center">
                <Button icon={PlusIcon} onClick={handleAddOfficeLocation}>
                  Add Office Location
                </Button>
              </Box>
            </EmptyListView>
          ) : (
            <Box borderColor="surface.border.gray.muted" borderWidth="thinner">
              <Table
                isLoading={isLoading}
                isRefreshing={isFetching}
                gridTemplateColumns="1fr 2fr 1fr 1fr 140px"
                data={{
                  nodes: officeLocations,
                }}
                pagination={
                  <TablePagination
                    currentPage={currentPage}
                    defaultPageSize={itemsPerPage as TablePaginationProps['defaultPageSize']}
                    paginationType="server"
                    showPageSizePicker
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    totalItemCount={data?.total_count ?? 0}
                  />
                }
                toolbar={
                  <TableToolbar
                    title={`Showing ${formatPaginationRange({
                      totalItems: data?.total_count ?? 0,
                      currentPage,
                      itemsPerPage,
                    })} Office Locations`}>
                    <Box>
                      <Button icon={PlusIcon} onClick={handleAddOfficeLocation}>
                        Add Office Location
                      </Button>
                    </Box>
                  </TableToolbar>
                }>
                {(tableData) => (
                  <>
                    <TableHeader>
                      <TableHeaderRow>
                        <TableHeaderCell>Office Name</TableHeaderCell>
                        <TableHeaderCell>Address</TableHeaderCell>
                        <TableHeaderCell>ESIC Dispensary</TableHeaderCell>
                        <TableHeaderCell>ESIC Sub-Code</TableHeaderCell>
                        <TableHeaderCell>Added On</TableHeaderCell>
                      </TableHeaderRow>
                    </TableHeader>

                    <TableBody>
                      {tableData.map((location) => (
                        <TableRow key={location.id} item={location}>
                          <TableCell>{location.office_name}</TableCell>
                          <TableCell>{`${location.address_line_1} ${location.address_line_2}, ${location.district}, ${location.state}, ${location.pincode}`}</TableCell>
                          <TableCell>{location.esic_dispensary}</TableCell>
                          <TableCell>{location.esic_sub_code}</TableCell>
                          <TableCell>
                            {getFormattedDateValue({
                              date: location.created_at,
                              formatString: dateFormats.humanDateMonthYear,
                            })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
              </Table>
            </Box>
          )}
        </Box>
      </ModalBody>
    </Modal>
  );
};
